import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  List,
  ListItem,
  FormControlLabel,
  ListItemSecondaryAction,
  Switch,
  ListItemText
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Close as CloseIcon,
  Quiz as QuizIcon,
  ExpandMore as ExpandMoreIcon,
  Comment as CommentIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import PdfUploadDialog from '@/components/admin/PdfUploadDialog';
import PdfReviewDialog from '@/components/admin/PdfReviewDialog';
import {
  useParsePdfMutation,
} from '@/store/api/adminApi';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import SectionInfoEditor from './SectionInfoEditor';
import DialogueQuestionGroupForm from './DialogueQuestionGroupForm';
import { QuestionForm } from '@/components/common';
import {
  openImagePreview,
  openRichTextPreview,
} from '@/store/slices/contentPreviewSlice';
import {
  useGetAttendanceTestQuestionsQuery,
  useCreateExerciseQuestionMutation,
  useUpdateExerciseQuestionMutation,
  useDeleteExerciseQuestionMutation,
  useGetSectionInfoQuery,
  useCreateSectionInfoMutation,
  useUpdateSectionInfoMutation,
  useCreateDialogueQuestionGroupMutation,
  useUpdateDialogueQuestionGroupMutation,
  useDeleteDialogueQuestionGroupMutation,
  useLazyGetDialogueQuestionGroupQuery,
  useBulkAddExerciseDayQuestionsMutation,
} from '@/store/api/admin/exerciseApi';
import { useSelector } from 'react-redux';
import { uploadMediaFiles } from '@/utils/cloudinaryUpload';

const TOEIC_PARTS = [
  { number: 1, title: 'Photographs', type: 'LC' },
  { number: 2, title: 'Question-Response', type: 'LC' },
  { number: 3, title: 'Conversations', type: 'LC' },
  { number: 4, title: 'Short Talks', type: 'LC' },
  { number: 5, title: 'Incomplete Sentences', type: 'RC' },
  { number: 6, title: 'Text Completion', type: 'RC' },
  { number: 7, title: 'Reading Comprehension', type: 'RC' },
];

const QUESTION_TYPES = {
  'multiple-choice': 'Multiple Choice',
  'fill-blank': 'Fill in the Blank',
  'short_answer': 'Short Answer',
  'dialogue-group': 'Dialogue Group (Fill-blanks + MC Questions)',
};

const AttendanceTestQuestionManagement = ({ 
  exerciseId, 
  dayId, 
  open, 
  onClose 
}) => {
  const [selectedParts, setSelectedParts] = useState([]);
  const [expandedPart, setExpandedPart] = useState(null);
  const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
  const [dialogKey, setDialogKey] = useState(0);
  const [dialogueGroupDialogOpen, setDialogueGroupDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editingDialogueGroup, setEditingDialogueGroup] = useState(null);
  const [editingPart, setEditingPart] = useState(null);
  const [formData, setFormData] = useState({});
  const formDataRef = useRef({});
  const [previousQuestion, setPreviousQuestion] = useState(null);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { uploadProgress } = useSelector((state) => state.upload);
  const questionFormRef = useRef(null);
  const dispatch = useDispatch();

  // API hooks
  const { 
    data: questionsResponse, 
    isLoading, 
    // error 
  } = useGetAttendanceTestQuestionsQuery({ dayId }, { 
    skip: !dayId,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });
  
  const [createQuestion, { isLoading: isCreating }] = useCreateExerciseQuestionMutation();
  const [updateQuestion, { isLoading: isUpdating }] = useUpdateExerciseQuestionMutation();
  const [deleteQuestion, { isLoading: isDeleting }] = useDeleteExerciseQuestionMutation();
  
  // Dialogue Group mutations
  const [createDialogueGroup] = useCreateDialogueQuestionGroupMutation();
  const [updateDialogueGroup] = useUpdateDialogueQuestionGroupMutation();
  const [deleteDialogueGroup] = useDeleteDialogueQuestionGroupMutation();
  const [getDialogueGroup] = useLazyGetDialogueQuestionGroupQuery();
  
  // Section Info API
  const sectionType = 'attendance-test';
  const { data: sectionInfoResponse } = useGetSectionInfoQuery(
    { dayId, sectionType },
    { skip: !dayId }
  );
  const [createSectionInfo] = useCreateSectionInfoMutation();
  const [updateSectionInfo] = useUpdateSectionInfoMutation();

  // PDF parser wiring (mirrors RC Exercise pattern).
  const [pdfUploadOpen, setPdfUploadOpen] = useState(false);
  const [pdfResultsOpen, setPdfResultsOpen] = useState(false);
  const [pdfParseResult, setPdfParseResult] = useState(null);
  const uploadAttendancePdfMutation = useParsePdfMutation();
  const bulkAddExerciseDayQuestionsTuple = useBulkAddExerciseDayQuestionsMutation();

  // Memoize questions to prevent unnecessary re-renders.
  // BE returns a flat array; the UI works with a partNumber-keyed map so
  // "active parts" can be derived from `Object.keys(questions)`.
  const questions = useMemo(() => {
    const raw = questionsResponse?.data;
    if (!raw) return {};
    if (Array.isArray(raw)) {
      const grouped = {};
      for (const q of raw) {
        const pn = Number(q?.partNumber);
        if (!Number.isInteger(pn) || pn < 1 || pn > 7) continue;
        if (!grouped[pn]) grouped[pn] = [];
        grouped[pn].push(q);
      }
      return grouped;
    }
    return raw;
  }, [questionsResponse?.data]);
  const sectionInfo = sectionInfoResponse?.data || {};

  const isAnyLoading = isLoading || isCreating || isUpdating || isDeleting;

  // Memoize getPartQuestions to prevent unnecessary recalculations
  const getPartQuestions = useMemo(() => {
    return (partNumber) => questions?.[partNumber] || [];
  }, [questions]);

  // Auto-select parts that have questions
  useEffect(() => {
    if (questions && typeof questions === 'object') {
      // Get unique part numbers from questions
      const partsWithQuestions = Object.keys(questions).map(
        (partNumber) => +partNumber
      );

      // Only update if different from current selection
      const currentSelection = [...selectedParts].sort().join(',');
      const newSelection = partsWithQuestions.sort().join(',');

      if (currentSelection !== newSelection) {
        setSelectedParts(partsWithQuestions);
        // Auto-expand the first part with questions
        if (partsWithQuestions.length > 0 && !expandedPart) {
          setExpandedPart(partsWithQuestions[0]);
        }
      }
    }
  }, [questions]);

  const handlePartToggle = (partNumber) => {
    setSelectedParts((prev) => {
      if (prev.includes(partNumber)) {
        return prev.filter((p) => p !== partNumber);
      } else {
        return [...prev, partNumber];
      }
    });
  };

  const handleExpandPart = (partNumber) => {
    setExpandedPart(expandedPart === partNumber ? null : partNumber);
  };

  const handleOpenQuestionDialog = (partNumber, question = null) => {
    setEditingPart(partNumber);
    setEditingQuestion(question);

    const partQuestions = getPartQuestions(partNumber);

    if (question) {
      // Find previous question for editing mode
      const currentIndex = partQuestions.findIndex(
        (q) => q._id === question._id
      );
      setPreviousQuestion(
        currentIndex > 0 ? partQuestions[currentIndex - 1] : null
      );
      
      // Try multiple possible field names for answers/options/choices
      const answersList = question.answers || question.choices || question.options || [];

      const options =
        answersList.length > 0
          ? answersList.map((a) => (a.text !== undefined ? a.text : a))
          : ["", "", "", ""];
      
      // Find correct answer - try multiple approaches
      let correctAnswer = "";
      if (question.correctAnswer) {
        // If correctAnswer is already set (like "A", "B", etc.)
        correctAnswer = question.correctAnswer.toLowerCase();
      } else {
        // Find from answers array
        const correctAnswerIndex = answersList.findIndex((a) => a.isCorrect);
        correctAnswer = correctAnswerIndex >= 0
          ? String.fromCharCode(65 + correctAnswerIndex).toLowerCase()
          : "";
      }

      setFormData({
        question_number: question.number || question.order || 1,
        question: question.title || '',
        content: question.mainContent || question.content || '',
        type: question.type || 'multiple-choice',
        options: options,
        correctAnswer: correctAnswer,
        blanks: question.blanks || [],
        explanation: question.explanation || '',
        audioPreview: question.audioUrl,
        imagePreview: question.imageUrl,
        extraImagePreview: question.extraImageUrl,
        partNumber: question.partNumber || partNumber,
      });
    } else {
      // Set previous question to the last question in the part for new question
      setPreviousQuestion(
        partQuestions.length > 0
          ? partQuestions[partQuestions.length - 1]
          : null
      );

      const nextOrder = partQuestions.length > 0 ? Math.max(...partQuestions.map(q => q.number || q.order || 0)) + 1 : 1;
      
      setEditingQuestion(null);
      setFormData({
        question_number: nextOrder,
        question: '',
        content: '',
        type: 'multiple-choice',
        options: ["", "", "", ""],
        correctAnswer: '',
        blanks: [],
        explanation: '',
        image: null,
        extraImage: null,
        audio: null,
        partNumber: partNumber,
      });
    }
    setError(null);
    setDialogKey((prev) => prev + 1);
    setQuestionDialogOpen(true);
  };


  const handleCloseQuestionDialog = () => {
    setQuestionDialogOpen(false);
    setEditingQuestion(null);
    setEditingPart(null);
    setPreviousQuestion(null);
    // Reset rich text editors
    if (questionFormRef.current) {
      questionFormRef.current.resetRichText();
    }
  };

  const handleFormDataChange = useCallback((updatedFormData) => {
    formDataRef.current = updatedFormData;
  }, []);

  const handleSubmitQuestion = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      setError(null);
      
      // Process images in rich text editors and get final data
      let currentFormData = formDataRef.current;
      if (questionFormRef.current) {
        currentFormData = await questionFormRef.current.processImagesAndGetData();
      }
      
      // Upload media files directly to Cloudinary
      const mediaFiles = {
        image: currentFormData.image,
        audio: currentFormData.audio,
        extraImage: currentFormData.extraImage,
      };
      
      const uploadedUrls = await uploadMediaFiles(mediaFiles, `toeic/exercises/${dayId}/attendance`);
      
      // Create FormData for file uploads
      const questionData = new FormData();
      
      // Append basic fields
      questionData.append('title', currentFormData.question);
      questionData.append('question', currentFormData.question);
      questionData.append('number', currentFormData.question_number);
      questionData.append('partNumber', editingPart);
      
      // Sanitize and append content
      const sanitizedContent = currentFormData.content
        ? currentFormData.content.replace(/<[^>]*>/g, "").trim()
        : "";
      questionData.append(
        'content',
        sanitizedContent.length > 0 ? currentFormData.content : ''
      );
      
      questionData.append('explanation', currentFormData.explanation || '');
      questionData.append('type', currentFormData.type || 'multiple-choice');
      
      // Handle different question types
      if (currentFormData.type === 'multiple-choice') {
        // Map options to choices format
        const choices = currentFormData.options.map((option, index) => {
          const value = String.fromCharCode(65 + index);
          return {
            text: option,
            value: value,
            isCorrect: value.toLowerCase() === currentFormData.correctAnswer?.toLowerCase(),
          };
        });

        const hasCorrectAnswer = choices.some((choice) => choice.isCorrect);
        if (!hasCorrectAnswer) {
          throw new Error('Please select the correct answer');
        }
        
        questionData.append('answers', JSON.stringify(choices));
        questionData.append('correctAnswer', currentFormData.correctAnswer.toUpperCase());
      } else if (currentFormData.type === 'fill-blank') {
        // For fill-blank questions
        if (!currentFormData.blanks || currentFormData.blanks.length === 0) {
          throw new Error('Please add at least one blank for fill-blank questions');
        }
        questionData.append('blanks', JSON.stringify(currentFormData.blanks));
      } else if (currentFormData.type === 'short_answer') {
        // For short answer questions - no additional validation needed
        questionData.append('correctAnswer', currentFormData.correctAnswer || '');
      } else if (currentFormData.type === 'dialogue') {
        // For dialogue questions - similar to multiple choice
        const choices = currentFormData.options.map((option, index) => {
          const value = String.fromCharCode(65 + index);
          return {
            text: option,
            value: value,
            isCorrect: value.toLowerCase() === currentFormData.correctAnswer?.toLowerCase(),
          };
        });

        const hasCorrectAnswer = choices.some((choice) => choice.isCorrect);
        if (!hasCorrectAnswer) {
          throw new Error('Please select the correct answer');
        }
        
        questionData.append('answers', JSON.stringify(choices));
        questionData.append('correctAnswer', currentFormData.correctAnswer.toUpperCase());
      }

      // Append uploaded URLs or existing URLs
      ['image', 'audio', 'extraImage'].forEach((mediaName) => {
        const url = `${mediaName}Url`;
        if (uploadedUrls[url]) {
          questionData.append(url, uploadedUrls[url]);
        } else if (Object.prototype.hasOwnProperty.call(currentFormData, url)) {
          questionData.append(url, currentFormData[url] ?? '');
        }
      });

      let result;
      if (editingQuestion) {
        result = await updateQuestion({
          questionId: editingQuestion._id,
          questionData,
        }).unwrap();
      } else {
        result = await createQuestion({
          dayId,
          partType: 'attendanceTest',
          partNumber: editingPart,
          questionData,
        }).unwrap();
      }

      toast.success(editingQuestion ? 'Question updated successfully' : 'Question created successfully');
      handleCloseQuestionDialog();
    } catch (error) {
      console.error('Error saving question:', error);
      setError(error.message || 'An error occurred while saving the question. Please try again.');
    }
    setIsSubmitting(false);
  };

  const handleSaveSectionInfo = async (sectionData) => {
    try {
      // Check if section info exists (has an _id or id)
      const sectionExists = sectionInfo?._id || sectionInfo?.id;
      
      if (sectionExists) {
        // Update existing section info
        await updateSectionInfo({
          dayId,
          sectionType,
          sectionData,
        }).unwrap();
      } else {
        // Create new section info
        await createSectionInfo({
          dayId,
          sectionType,
          sectionData,
        }).unwrap();
      }
      // Success toast is handled in SectionInfoEditor
    } catch (error) {
      throw error; // Re-throw to be handled by SectionInfoEditor
    }
  };

  const handleDeleteQuestion = async (partNumber, questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) {
      return;
    }

    try {
      await deleteQuestion(questionId).unwrap();
      toast.success('Question deleted successfully');
    } catch (error) {
      console.error('Failed to delete question:', error);
      toast.error(error?.data?.message || 'Failed to delete question');
    }
  };

  // Dialogue Group Handlers
  const handleOpenDialogueGroupDialog = (partNumber) => {
    setEditingPart(partNumber);
    
    // Calculate next question number for new dialogue group
    const partQuestions = getPartQuestions(partNumber);
    const nextNumber =
      partQuestions.length > 0
        ? Math.max(...partQuestions.map((q) => q.number || 0)) + 1
        : 1;
    
    // Set initial data with auto-filled question number
    setEditingDialogueGroup({ nextQuestionNumber: nextNumber });
    setDialogueGroupDialogOpen(true);
  };

  const handleEditDialogueGroup = async (parentQuestion) => {
    try {
      // Fetch full dialogue group data with child questions
      const fullData = await getDialogueGroup(parentQuestion._id).unwrap();
      
      // Store the full data with the parent question ID
      const dialogueGroupData = {
        ...fullData.data,
        parentQuestionId: parentQuestion._id, // Store the ID for updates
      };
      
      setEditingDialogueGroup(dialogueGroupData);
      setEditingPart(parentQuestion.partNumber || 3);
      setDialogueGroupDialogOpen(true);
    } catch (error) {
      console.error("Failed to fetch dialogue group:", error);
      toast.error("Failed to load dialogue group data");
    }
  };

  const handleCloseDialogueGroupDialog = () => {
    setDialogueGroupDialogOpen(false);
    setEditingDialogueGroup(null);
  };

  const handleSubmitDialogueGroup = async (dialogueData) => {
    try {
      if (editingDialogueGroup?.parentQuestionId) {
        // Update existing dialogue group
        const parentQuestionId = editingDialogueGroup.parentQuestionId || 
                                 editingDialogueGroup.parentQuestion?._id ||
                                 editingDialogueGroup._id;
        
        await updateDialogueGroup({
          parentQuestionId,
          dialogueData,
        }).unwrap();
        
        toast.success("Dialogue group updated successfully");
      } else {
        // Create new dialogue group
        await createDialogueGroup(dialogueData).unwrap();
        toast.success("Dialogue group created successfully");
      }
      handleCloseDialogueGroupDialog();
    } catch (error) {
      console.error("Failed to save dialogue group:", error);
      toast.error(error?.data?.message || "Failed to save dialogue group");
    }
  };

  const handleDeleteDialogueGroup = async (parentQuestionId) => {
    if (!window.confirm("Are you sure you want to delete this dialogue group? This will delete the parent question and all child questions.")) {
      return;
    }

    try {
      await deleteDialogueGroup(parentQuestionId).unwrap();
      toast.success("Dialogue group deleted successfully");
    } catch (error) {
      console.error("Failed to delete dialogue group:", error);
      toast.error(error?.data?.message || "Failed to delete dialogue group");
    }
  };


  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <QuizIcon />
            <Typography variant="h5">
              Attendance Test - Question Management
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadFileIcon />}
              onClick={() => setPdfUploadOpen(true)}
            >
              Upload PDF
            </Button>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box>
          {/* Section Info Editor */}
          <SectionInfoEditor
            sectionType={sectionType}
            dayId={dayId}
            initialData={sectionInfo}
            onSave={handleSaveSectionInfo}
          />

          {/* Loading State */}
          {isLoading && (
            <Box display="flex" justifyContent="center" my={3}>
              <CircularProgress />
            </Box>
          )}

          {/* Error State */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to load questions: {error?.data?.message || error.message}
            </Alert>
          )}

          {/* Questions Header */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">Questions by Part</Typography>
            <Chip
              label={`${Object.values(questions).flat().length} Total Questions`}
              color="primary"
            />
          </Box>

          {/* Part Selection */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Select TOEIC Parts to Include
              </Typography>
              <Grid container spacing={2}>
                {TOEIC_PARTS.map((part) => (
                  <Grid item xs={12} sm={6} md={4} key={part.number}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={selectedParts.includes(+part.number)}
                          onChange={() => handlePartToggle(+part.number)}
                          color="primary"
                          disabled={isAnyLoading}
                        />
                      }
                      label={`Part ${part.number}: ${part.title}`}
                    />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* Parts and Questions */}
          {selectedParts
            .sort((a, b) => a - b)
            .map((partNumber) => {
              const part = TOEIC_PARTS.find(
                (p) => +p.number === +partNumber
              );
              const partQuestions = getPartQuestions(partNumber);
              return (
                <Accordion
                  key={partNumber}
                  expanded={expandedPart === partNumber}
                  onChange={() => handleExpandPart(partNumber)}
                  sx={{ mb: 2 }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      width="100%"
                    >
                      <Typography variant="h6">
                        Part {part?.number}: {part?.title}
                      </Typography>
                      <Chip
                        label={`${partQuestions.length} Questions`}
                        color="primary"
                        size="small"
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box>
                      <Box display="flex" gap={2} mb={2}>
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => handleOpenQuestionDialog(partNumber)}
                          disabled={isAnyLoading}
                        >
                          Add Question
                        </Button>
                        
                        {/* Show Dialogue Group button only for Parts 3 & 4 */}
                        {(partNumber === 3 || partNumber === 4) && (
                          <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<CommentIcon />}
                            onClick={() => handleOpenDialogueGroupDialog(partNumber)}
                            disabled={isAnyLoading}
                          >
                            Add Dialogue Group
                          </Button>
                        )}
                      </Box>

                      {/* Questions List */}
                      {partQuestions.length === 0 ? (
                        <Alert severity="info">
                          No questions added yet. Click "Add Question" to create the first question for this part.
                        </Alert>
                      ) : (
                        <List>
                          {partQuestions.map((question, index) => (
                            <ListItem key={question._id || question.id} divider>
                              <ListItemText
                                primary={
                                  <Box>
                                    <Typography variant="body1" fontWeight="medium" noWrap>
                                      {question.number || question.order || index + 1}. {question.title || question.question}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ ml: 1 }}
                                    >
                                      ({QUESTION_TYPES[question.type] || "Unknown"})
                                    </Typography>
                                  </Box>
                                }
                                secondary={
                                  <Box>
                                    <Typography variant="body2" color="text.secondary">
                                      {question.type === "multiple-choice" && question.correctAnswer && (
                                        <>
                                          Correct Answer: {question.correctAnswer}
                                          {question.choices?.find((a) => a.isCorrect) && (
                                            <>. {question.choices.find((a) => a.isCorrect).text}</>
                                          )}
                                        </>
                                      )}
                                      {question.type === "fill-blank" && question.blanks && (
                                        <>
                                          Blanks: {question.blanks.length} blank(s)
                                        </>
                                      )}
                                      {question.type === "short_answer" && question.correctAnswer && (
                                        <>
                                          Expected Answer: {question.correctAnswer}
                                        </>
                                      )}
                                      {question.type === "dialogue-group" && (
                                        <>
                                          Dialogue Group: {question.childQuestions?.length || 0} child question(s)
                                        </>
                                      )}
                                    </Typography>
                                    {question.audioUrl && (
                                      <audio
                                        controls
                                        src={question.audioUrl}
                                        style={{
                                          maxWidth: "100%",
                                          maxHeight: "200px",
                                          borderRadius: "4px",
                                          width: "100%",
                                          marginTop: "8px",
                                        }}
                                      />
                                    )}
                                    <Box display="flex" gap={1} mt={1}>
                                      {question.imageUrl && (
                                        <IconButton
                                          size="small"
                                          aria-label="image"
                                          onClick={() =>
                                            dispatch(openImagePreview(question.imageUrl))
                                          }
                                        >
                                          <ImageIcon />
                                        </IconButton>
                                      )}
                                      {question.extraImageUrl && (
                                        <IconButton
                                          size="small"
                                          aria-label="extra image"
                                          onClick={() =>
                                            dispatch(openImagePreview(question.extraImageUrl))
                                          }
                                        >
                                          <ImageIcon color="secondary" />
                                        </IconButton>
                                      )}
                                      {question.content && question.content !== "<br>" && (
                                        <IconButton
                                          size="small"
                                          aria-label="content"
                                          onClick={() =>
                                            dispatch(openRichTextPreview(question.content))
                                          }
                                        >
                                          <CommentIcon />
                                        </IconButton>
                                      )}
                                    </Box>
                                  </Box>
                                }
                              />
                              <ListItemSecondaryAction>
                                <IconButton
                                  edge="end"
                                  color="primary"
                                  onClick={() => {
                                    if (question.type === 'dialogue-group') {
                                      handleEditDialogueGroup(question);
                                    } else {
                                      handleOpenQuestionDialog(partNumber, question);
                                    }
                                  }}
                                  disabled={isAnyLoading}
                                  sx={{ mr: 1 }}
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  edge="end"
                                  color="error"
                                  onClick={() => {
                                    if (question.type === 'dialogue-group') {
                                      handleDeleteDialogueGroup(question._id || question.id);
                                    } else {
                                      handleDeleteQuestion(partNumber, question._id || question.id);
                                    }
                                  }}
                                  disabled={isAnyLoading}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </ListItemSecondaryAction>
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })
}
        </Box>
      </DialogContent>

      {/* Question Form Dialog */}
      <Dialog 
        open={questionDialogOpen} 
        onClose={handleCloseQuestionDialog} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle component="div">
          <Typography variant="h6">
            {editingQuestion ? 'Edit Question' : 'Add New Question'}
          </Typography>
        </DialogTitle>
        {error && (
          <Alert
            severity="error"
            onClose={() => setError(null)}
            sx={{ m: 2, mb: 0 }}
          >
            {error}
          </Alert>
        )}
        <DialogContent>
          <QuestionForm
            key={dialogKey}
            ref={questionFormRef}
            initialData={formData}
            onChange={handleFormDataChange}
            previousQuestion={previousQuestion}
            showContent={true}
            isEditing={!!editingQuestion}
            allowMultipleTypes={true}
          />
        </DialogContent>
        <DialogActions>
          {isSubmitting && (
            <LinearProgress
              variant={uploadProgress > 0 ? "determinate" : "indeterminate"}
              value={uploadProgress}
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                borderTopLeftRadius: 4,
                borderTopRightRadius: 4,
              }}
            />
          )}
          <Button disabled={isSubmitting} onClick={handleCloseQuestionDialog}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitQuestion}
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting && <CircularProgress size={16} sx={{ mr: 1 }} />}
            {editingQuestion ? 'Update' : 'Add'} Question
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogue Question Group Dialog */}
      <Dialog
        open={dialogueGroupDialogOpen}
        onClose={handleCloseDialogueGroupDialog}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">
            {editingDialogueGroup?.parentQuestionId ? 'Edit Dialogue Group' : 'Add Dialogue Group'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogueQuestionGroupForm
            dayId={dayId}
            partType="attendanceTest"
            partNumber={editingPart}
            initialData={editingDialogueGroup}
            onSubmit={handleSubmitDialogueGroup}
            onCancel={handleCloseDialogueGroupDialog}
          />
        </DialogContent>
      </Dialog>

      {/* PDF Upload (Attendance Test) */}
      <PdfUploadDialog
        open={pdfUploadOpen}
        onClose={() => setPdfUploadOpen(false)}
        title="Parse PDF — Attendance Test"
        description="Upload a TOEIC mock-test PDF for this day's Attendance Test. Optionally restrict parsing to a single PART (1–7) to speed things up. Nothing is saved yet — you will review and confirm before questions are added."
        uploadMutation={uploadAttendancePdfMutation}
        defaultFormat="auto"
        defaultOcrLang="eng"
        extraFields={[
          {
            name: 'part',
            label: 'PART to parse',
            type: 'select',
            defaultValue: '',
            options: [
              { value: '', label: 'All Parts (1–7)' },
              { value: '1', label: 'PART 1 — Photographs (LC)' },
              { value: '2', label: 'PART 2 — Question-Response (LC)' },
              { value: '3', label: 'PART 3 — Conversations (LC)' },
              { value: '4', label: 'PART 4 — Short Talks (LC)' },
              { value: '5', label: 'PART 5 — Incomplete Sentences (RC)' },
              { value: '6', label: 'PART 6 — Text Completion (RC)' },
              { value: '7', label: 'PART 7 — Reading Comprehension (RC)' },
            ],
          },
        ]}
        onParsed={(payload) => {
          setPdfParseResult(payload);
          setPdfResultsOpen(true);
        }}
      />

      <PdfReviewDialog
        open={pdfResultsOpen}
        onClose={() => setPdfResultsOpen(false)}
        parsed={pdfParseResult}
        targetLabel={`Exercise Day ${dayId} — Attendance Test${
          pdfParseResult?.part ? ` (Part ${pdfParseResult.part})` : ''
        }`}
        sectionDefault="attendance-test"
        defaultPartNumber={pdfParseResult?.part || undefined}
        uploadFolder={`toeic/exercises/${dayId}/attendance`}
        showContent
        submitMutation={bulkAddExerciseDayQuestionsTuple}
        buildBulkPayload={({ questions: payloadQuestions, parsed }) => {
          // If admin restricted parsing to a single PART, send it as the
          // top-level partNumber so every question inherits it. Otherwise
          // rely on per-item `partNumber` from the parser (all-parts mode).
          const topLevelPart =
            parsed?.part && Number.isInteger(Number(parsed.part))
              ? Number(parsed.part)
              : undefined;
          return {
            dayId,
            target: 'attendance-test',
            ...(topLevelPart ? { partNumber: topLevelPart } : {}),
            questions: payloadQuestions.map((q) => ({
              ...q,
              partNumber: q.partNumber ?? topLevelPart,
            })),
          };
        }}
      />

    </Dialog>
  );
};

export default AttendanceTestQuestionManagement;
